<template>
  <div class="resume-manage-container">
    <div class="page-header">
      <div>
        <h2>简历数据</h2>
        <p>查看候选人基础信息、联系方式和简历详情</p>
      </div>
    </div>

    <el-card class="box-card table-card">
      <!-- 搜索栏 -->
      <div class="search-wrapper">
        <el-input
          v-model="keyword"
          placeholder="请输入关键词搜索（姓名、手机号、邮箱等）"
          clearable
          class="search-input"
          @keyup.enter="handleSearch"
        >
          <template #prefix>
            <el-icon><Search /></el-icon>
          </template>
        </el-input>
        <el-button
          type="primary"
          :icon="Search"
          @click="handleSearch"
          class="search-btn"
        >
          搜索
        </el-button>
        <el-button :icon="Refresh" @click="handleReset" class="reset-btn"
          >重置</el-button
        >
      </div>

      <!-- 简历列表表格 -->
      <el-table
        v-loading="loading"
        :data="resumeList"
        stripe
        style="width: 100%"
        :empty-text="emptyText"
      >
        <el-table-column type="index" label="序号" width="60" align="center" />
        <el-table-column label="用户名">
          <template #default="{ row }">
            {{ row.userId?.username || '-' }}
          </template>
        </el-table-column>
        <el-table-column label="真实姓名">
          <template #default="{ row }">
            {{ getNestedValue(row, 'resumeData.baseInfo.name') }}
          </template>
        </el-table-column>
        <el-table-column label="头像" width="100" align="center">
          <!-- 展示图片 -->
          <template #default="{ row }">
            <el-image
              :src="getNestedValue(row, 'resumeData.baseInfo.avatar')"
              fit="cover"
              class="avatar-image"
              :preview-src-list="[
                getNestedValue(row, 'resumeData.baseInfo.avatar')
              ]"
              :initial-index="0"
              preview-teleported
            />
          </template>
        </el-table-column>
        <el-table-column label="性别" width="80" align="center">
          <template #default="{ row }">
            {{ getNestedValue(row, 'resumeData.baseInfo.gender') }}
          </template>
        </el-table-column>
        <el-table-column label="手机号" width="130" align="center">
          <template #default="{ row }">
            {{ getNestedValue(row, 'resumeData.baseInfo.phone') || '-' }}
          </template>
        </el-table-column>
        <el-table-column label="邮箱" min-width="180">
          <template #default="{ row }">
            {{ getNestedValue(row, 'resumeData.baseInfo.email') || '-' }}
          </template>
        </el-table-column>
        <el-table-column label="工作年限" width="100" align="center">
          <template #default="{ row }">
            {{ getNestedValue(row, 'resumeData.baseInfo.workHours') }}
          </template>
        </el-table-column>
        <el-table-column label="出生年月" width="100" align="center">
          <template #default="{ row }">
            {{ getNestedValue(row, 'resumeData.baseInfo.birthday') }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="180" align="center">
          <template #default="{ row }">
            <div class="action-buttons">
              <el-button type="primary" size="small" @click="handleView(row)"
                >查看</el-button
              >
              <el-button type="danger" size="small" @click="handleDelete(row)"
                >删除</el-button
              >
            </div>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页组件 -->
      <div class="pagination-wrapper">
        <el-pagination
          v-model:current-page="page"
          v-model:page-size="limit"
          :page-sizes="[10, 20, 50, 100]"
          :total="total"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </el-card>
  </div>
</template>

<script setup>
import { getResumeList } from '@/api/resume'
import { ref, onMounted } from 'vue'
import { Search, Refresh } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import { useRouter } from 'vue-router'

const router = useRouter()
const resumeList = ref([])
const total = ref(0)
const page = ref(1)
const limit = ref(10)
const keyword = ref('')
const loading = ref(false)
const emptyText = ref('暂无数据')

/**
 * 获取嵌套对象的值
 */
const getNestedValue = (obj, path) => {
  if (!obj || !path) return ''
  const keys = path.split('.')
  let value = obj
  for (const key of keys) {
    value = value?.[key]
    if (value === undefined || value === null) return ''
  }
  return value
}

/**
 * 获取简历列表数据
 */
const getResumeListData = async () => {
  loading.value = true
  emptyText.value = '加载中...'
  const res = await getResumeList(page.value, limit.value, keyword.value)
  resumeList.value = res.data || []
  total.value = res.total || 0
  emptyText.value = '暂无数据'
  loading.value = false
}

/**
 * 搜索处理
 */
const handleSearch = () => {
  page.value = 1
  getResumeListData()
}

/**
 * 重置搜索
 */
const handleReset = () => {
  keyword.value = ''
  page.value = 1
  getResumeListData()
}

/**
 * 分页大小改变
 */
const handleSizeChange = (val) => {
  limit.value = val
  page.value = 1
  getResumeListData()
}

/**
 * 当前页改变
 */
const handleCurrentChange = (val) => {
  page.value = val
  getResumeListData()
}

/**
 * 查看简历详情
 */
const handleView = (row) => {
  if (row._id) {
    router.push({
      path: '/resume-detail',
      query: {
        resumeId: row.resumeId
      }
    })
  } else {
    ElMessage.warning('简历ID不存在，无法查看详情')
  }
}

/**
 * 删除简历
 */
const handleDelete = () => {
  ElMessage.info('暂不支持删除功能')
}

// 初始化加载数据
onMounted(() => {
  getResumeListData()
})
</script>

<style scoped lang="scss">
.resume-manage-container {
  .box-card {
    overflow: hidden;

    .search-wrapper {
      .search-input {
        flex: 1;
        max-width: 500px;
      }
    }
  }
}

:deep(.el-table) {
  border-radius: 8px;
  overflow: hidden;

  .el-table__header {
    th {
      font-weight: 600;
    }
  }

  .el-table__row {
    &:hover {
      background-color: #f8fafc;
    }

    td {
      padding: 12px 0;
    }
  }

  .el-table__empty-block {
    min-height: 200px;
  }
}

:deep(.el-pagination) {
  .el-pagination__total,
  .el-pagination__sizes,
  .el-pagination__jump {
    margin-right: 16px;
  }
}

.search-btn {
  min-width: 80px;
}

.reset-btn {
  min-width: 80px;
}

.avatar-image {
  width: 48px;
  height: 48px;
  border-radius: 8px;
  object-fit: cover;
  border: 1px solid var(--admin-color-border-soft);
}

:deep(.el-image-viewer__wrapper) {
  .el-image-viewer__img {
    border-radius: 0;
    max-width: 80%;
    max-height: 80%;
  }
}
</style>
